/**
 * 저장 공간 경고 줄과 그것을 묻는 훅 — 화면 상세의 편성 배정·목록의 일괄 배정·바로 보내기가 같이 쓴다.
 *
 * 규칙과 문구는 `storageCheck.ts` 에 있다. 여기는 묻고 그리는 쪽이다.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { SignageOneClient, StorageCheckInput } from '../api/client.js';
import { useDebounced } from '../hooks.js';
import { storageCheckKey, storageShortfalls, storageWarningText, type StorageShortfall } from './storageCheck.js';

/**
 * 이 편성이 이 화면들에 안 들어가는지 묻는다 — 모자란 화면 목록. 묻는 중이거나 모르면 빈 목록.
 *
 * 고르는 손을 따라 매번 묻지 않게 잠깐 기다린다(드롭다운을 훑거나 콘텐츠를 연달아 고를 때). 실패는
 * 삼킨다 — 이것은 참고로 붙이는 경고라, 못 물었다고 배정 화면에 오류를 띄울 일이 아니다.
 */
export function useStorageCheck(client: SignageOneClient, input: StorageCheckInput | null): StorageShortfall[] {
  const key = storageCheckKey(input);
  const settled = useDebounced(key, 300);
  const latest = useRef(input);
  latest.current = input;
  const [answer, setAnswer] = useState<{ key: string; shortfalls: StorageShortfall[] } | null>(null);

  useEffect(() => {
    const asking = latest.current;
    // 기다리는 사이 또 바뀌었으면 이번 것은 묻지 않는다 — 바뀐 것이 멎으면 그때 묻는다.
    if (!settled || !asking || storageCheckKey(asking) !== settled) return;
    let alive = true;
    client
      .storageCheck(asking)
      .then((result) => {
        if (alive) setAnswer({ key: settled, shortfalls: storageShortfalls(result) });
      })
      .catch(() => {
        if (alive) setAnswer({ key: settled, shortfalls: [] });
      });
    return () => {
      alive = false;
    };
  }, [client, settled]);

  // 지금 고른 것에 대한 답만 보인다 — 고르는 사이에 앞의 답이 남아 엉뚱한 화면을 가리키지 않게.
  return answer && answer.key === key ? answer.shortfalls : [];
}

/** 모자란 화면이 있으면 한 줄. 없거나 모르면 아무것도 그리지 않는다. */
export function StorageWarning({ shortfalls }: { shortfalls: readonly StorageShortfall[] }): ReactNode {
  const text = storageWarningText(shortfalls);
  if (!text) return null;
  return (
    <div className="note note-warn" role="status">
      <span>
        <strong>저장 공간이 모자란 화면 {shortfalls.length}대</strong> — {text}. 그대로 진행할 수 있지만, 화면이
        받다가 멈추거나 받아 둔 다른 파일을 지울 수 있습니다.
      </span>
    </div>
  );
}
